import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { User, UserRole } from '../types'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  currentUser: User | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string, phone: string) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateUserProfile: (data: Partial<User>) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch user profile from Firestore
  const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
      if (userDoc.exists()) {
        return { id: firebaseUser.uid, ...userDoc.data() } as unknown as User
      }
      return null
    } catch (error) {
      console.error('Error fetching user profile:', error)
      return null
    }
  }

  // Create user profile in Firestore
  const createUserProfile = async (firebaseUser: FirebaseUser, additionalData: any = {}) => {
    try {
      const userProfile: Omit<User, 'id'> = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || additionalData.name || '',
        phone: additionalData.phone || '',
        role: ['admin@starwaveenergu.live', 'admin@starwaveenergy.live'].includes(firebaseUser.email || '')
          ? 'admin'
          : 'customer' as UserRole,
        profile: {
          name: additionalData.name || firebaseUser.displayName || '',
          phone: additionalData.phone || '',
          address: additionalData.address || null,
          preferences: {
            notifications: true,
            newsletter: false,
            language: 'en'
          }
        },
        createdAt: new Date() as any,
        updatedAt: new Date() as any
      }

      await setDoc(doc(db, 'users', firebaseUser.uid), userProfile)
      return { id: firebaseUser.uid, ...userProfile } as User
    } catch (error) {
      console.error('Error creating user profile:', error)
      throw error
    }
  }

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('Successfully logged in!')
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.message || 'Failed to log in')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Register function
  const register = async (email: string, password: string, name: string, phone: string) => {
    try {
      setLoading(true)
      const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password)

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, {
        displayName: name
      })

      // Create user profile in Firestore
      await createUserProfile(firebaseUser, { name, phone })

      toast.success('Account created successfully!')
    } catch (error: any) {
      console.error('Registration error:', error)
      toast.error(error.message || 'Failed to create account')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth)
      setCurrentUser(null)
      setFirebaseUser(null)
      toast.success('Successfully logged out!')
    } catch (error: any) {
      console.error('Logout error:', error)
      toast.error('Failed to log out')
      throw error
    }
  }

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
      toast.success('Password reset email sent!')
    } catch (error: any) {
      console.error('Password reset error:', error)
      toast.error(error.message || 'Failed to send password reset email')
      throw error
    }
  }

  // Update user profile function
  const updateUserProfile = async (data: Partial<User>) => {
    if (!currentUser) throw new Error('No user logged in')

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...data,
        updatedAt: new Date()
      })

      setCurrentUser(prev => prev ? { ...prev, ...data } : null)
      toast.success('Profile updated successfully!')
    } catch (error: any) {
      console.error('Profile update error:', error)
      toast.error('Failed to update profile')
      throw error
    }
  }

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setLoading(true)

        if (firebaseUser) {
          setFirebaseUser(firebaseUser)

          // Fetch or create user profile
          let userProfile = await fetchUserProfile(firebaseUser)

          // If no profile exists, try to create one (backfill)
          if (!userProfile) {
            try {
              userProfile = await createUserProfile(firebaseUser)
            } catch (err) {
              console.error("Failed to auto-create profile for existing auth user:", err)
              // Fallback: don't crash, just set null so user can potentially logout/fix
              userProfile = null
            }
          }

          setCurrentUser(userProfile)
        } else {
          setFirebaseUser(null)
          setCurrentUser(null)
        }
      } catch (err) {
        console.error("Auth state change error:", err)
      } finally {
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const value: AuthContextType = {
    user: currentUser, // Add this alias for compatibility
    currentUser,
    firebaseUser,
    loading,
    login,
    register,
    logout,
    resetPassword,
    updateUserProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}